import React, { useEffect, useState } from 'react';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ReactRoutes from '../../constants/react-routes';
import TranslationKeys from '../../constants/translation-keys';
import '../.././styles/alumni.scss';
import AlumniLogo from '../../images/logo.svg';
import { recordEvent } from 'src/analytics/amplify';
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { IconArrowLeft, IconCheckCircleFill } from "@amzn/stencil-react-icons";
import { useSendInviteMutation } from "src/services/invite";
import { usePrincipal } from "src/hooks";

const RESEND_DURATION = 30;

export default function VerifyAccountView() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [principal] = usePrincipal();
    const [showButton, setShowButton] = useState(true);
    const [timer, setTimer] = useState(30);
    const [sendInvite] = useSendInviteMutation({
        fixedCacheKey: 'send-invite-button-mutation',
    });

    useEffect(() => {
        recordEvent({
            name: 'VerifyAccountView.Render',
        });
    }, []);

    function handleBackButtonClick() {
        recordEvent({
            name: 'VerifyAccountView.BackButton.Click',
            immediate: true
        });
        navigate({ pathname: ReactRoutes.ALUMNI_CREATE_ACCOUNT });
    }

    function handleResend() {
        setShowButton(false);
        let countdown = RESEND_DURATION;
        const interval = setInterval(() => {
            countdown -= 1;
            setTimer(countdown);
            if (countdown <= 0) {
                clearInterval(interval);
                setShowButton(true);
                setTimer(RESEND_DURATION);
            }
        }, 1000);
        recordEvent({
            name: 'VerifyAccountView.ResendButton.Click',
            immediate: true
        });
        sendInvite({
            principal,
        }).unwrap()
            .then(() => {
                recordEvent({
                    name: 'ResendButton.Click.Success',
                    immediate: true,
                })
            })
            .catch(error => {
                recordEvent({
                    name: 'ResendButton.Click.Failure',
                    immediate: true,
                })
                console.error('failed to re-send invite', error)
            })
    }

    return (
        <View className="alumni-content">
            <Col gridGap="S300">
                <Row gridGap="S500">
                    <Button
                        autoFocus={true}
                        size={ButtonSize.Default}
                        style={{ color: '#232F3E' }}
                        data-testid='verifyAccountBackButton'
                        icon={<IconArrowLeft aria-hidden={true} />}
                        variant={ButtonVariant.Tertiary}
                        aria-label="Go back to create account screen"
                        onClick={handleBackButtonClick}>
                    </Button>
                </Row>
                <View className="logo">
                    <img src={AlumniLogo} alt="Amazon A to Z" />
                </View>
                <View className="header">
                    <div role="heading" aria-level={1} data-testid="verifyAccountHeader">
                        {t(TranslationKeys.UI.ALUMNI.VERIFY_ACCOUNT_HEADER)}
                    </div>
                </View>
                <View className="description">
                    <div data-testid="verifyAccountBody">
                        {t(TranslationKeys.UI.ALUMNI.VERIFY_ACCOUNT_DESCRIPTION)}
                    </div>
                </View>
                <View className="description" data-testid="verifyAccountHelpMessage">
                    {t(TranslationKeys.UI.ALUMNI.VERIFY_ACCOUNT_HELP_MESSAGE)}
                </View>
                <Row gridGap="S500">
                    {showButton ? (<Button
                        data-testid="resendEmailButton"
                        variant={ButtonVariant.Tertiary}
                        size={ButtonSize.Small}
                        className="resend-email-button"
                        onClick={handleResend}>
                        {t(TranslationKeys.UI.BUTTONS.RESEND_EMAIL)}
                    </Button>) :
                        <View className="description" data-testid='resendEmailDescription'>
                            <Row gridGap="S100">
                                <IconCheckCircleFill aria-hidden={true} color="green70" /> <b>{t(TranslationKeys.UI.ALUMNI.RESEND_EMAIL_HEADER, { timer })}</b>
                            </Row>
                        </View>
                    }
                </Row>
            </Col>
        </View>
    )
}
